import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react'
import type { ReactNode } from 'react'

import { cn } from '../cn'

export type AlertTone = 'info' | 'success' | 'warning' | 'danger'

const TONE: Record<AlertTone, { box: string; icon: ReactNode }> = {
  info: { box: 'border-info/30 bg-info-bg text-ink', icon: <Info className="text-info" /> },
  success: {
    box: 'border-recovered/30 bg-recovered-bg text-ink',
    icon: <CheckCircle2 className="text-recovered" />,
  },
  warning: {
    box: 'border-atrisk/40 bg-atrisk-bg text-ink',
    icon: <AlertTriangle className="text-atrisk" />,
  },
  danger: { box: 'border-lost/30 bg-lost-bg text-ink', icon: <XCircle className="text-lost" /> },
}

export interface AlertProps {
  readonly tone?: AlertTone
  readonly title?: ReactNode
  readonly children?: ReactNode
  readonly actions?: ReactNode
  readonly className?: string
  /** Errors/warnings are announced assertively; info/success politely. */
  readonly live?: boolean
}

export function Alert({
  tone = 'info',
  title,
  children,
  actions,
  className,
  live = true,
}: AlertProps) {
  const role = tone === 'danger' || tone === 'warning' ? 'alert' : 'status'
  return (
    <div
      role={live ? role : undefined}
      className={cn(
        'flex gap-3 rounded-sm border px-3.5 py-3 text-[13px]',
        TONE[tone].box,
        className,
      )}
    >
      <span aria-hidden className="mt-0.5 shrink-0 [&>svg]:size-4">
        {TONE[tone].icon}
      </span>
      <div className="min-w-0 flex-1">
        {title !== undefined && <div className="font-semibold">{title}</div>}
        {children !== undefined && (
          <div className={cn(title !== undefined && 'mt-0.5', 'text-muted')}>{children}</div>
        )}
        {actions !== undefined && <div className="mt-2 flex flex-wrap gap-2">{actions}</div>}
      </div>
    </div>
  )
}
